import React from 'react'
import './privacy-and-terms.scss'
import Navbar from '../Navbar/Navbar'
import Footer from '../Footer/Footer'
import SEO from '../SEO/SEO'

const TermsCondition = () => {
  return (
    <>
      <SEO
        title="Terms and Conditions | Teleport Now"
        description="Read the terms governing Teleport Now accounts, travel services, bookings, payments, cancellations, acceptable use and related customer responsibilities."
        canonicalPath="/terms-and-conditions"
      />
      <Navbar />
      <div className="privacy-and-terms">
        <div className="container">
          <div className="header-title">
            <div className="overlay">
              <h2>Terms and Conditions</h2>
              <p className="legal-hero-meta">Effective Date: <span>10 October 2025</span></p>
            </div>
          </div>

          <div className="page-content">
            <div className="term-heading">
              <h4>Terms and Conditions</h4>
              <p>
                Effective Date: <span>10 October 2025</span>
              </p>
            </div>

            <div className="point-wrap">
              {/* INTRODUCTION */}
              <h2>Introduction</h2>
              <p>
                These Terms and Conditions (“Terms”) constitute a legally binding
                agreement between you (“you”, “your,” or “Customer”) and{" "}
                <b>TELEPORT NOW (SG) PTE. LTD.</b> (“Teleport Now,” “we,” “our,” or “us”),
                a company incorporated in the Republic of Singapore (UEN:
                202519519R) with its registered address at 60 Paya Lebar Road,
                #06-28 Paya Lebar Square, Singapore 409051.
              </p>
              <p>
                By accessing, registering, or using Teleport Now’s Services in any
                manner, you acknowledge and agree that you have read, understood,
                and unconditionally accept these Terms and our Privacy Policy. If
                you do not agree, you must immediately cease all use of Teleport Now’s
                Services.
              </p>
              <p>
                These Terms apply to all access and use of our websites, mobile
                applications, and associated services, and govern all interactions
                between you and Teleport Now.
              </p>

              <hr />

              {/* SCOPE OF SERVICES */}
              <h2>Scope of Services</h2>
              <p>Teleport Now provides the following core services:</p>

              <h3>1. eSIM Services</h3>
              <p>
                Teleport Now offers digital eSIM (embedded SIM) products that allow
                users to access mobile data services through supported devices. Our
                eSIMs provide temporary or long-term data plans, often on a prepaid
                basis, and may include offerings from local and international
                telecom operators. Data availability, performance, and coverage
                vary depending on country, carrier, and device specifications.
                Teleport Now does not guarantee uninterrupted data connectivity.
              </p>
              <p>
                <b>Special Requirement (Singapore):</b> For eSIMs that include{" "}
                <b>network access in Singapore</b>, users are required to complete a{" "}
                <b>one-time electronic Know-Your-Customer (eKYC) verification</b>{" "}
                process, in compliance with Singapore laws and regulations. Failure
                to complete this verification may result in denial of service.
              </p>

              <h3>2. Flight Booking</h3>
              <p>
                Teleport Now facilitates the search, selection, and booking of domestic
                and international flights via third-party travel service providers.
              </p>
              <h4>Important Conditions for Flight Bookings:</h4>
              <ul>
                <li>
                  <b>Two-Step Confirmation:</b> Flight bookings are confirmed only
                  after you receive (1) a payment successful email, followed by (2)
                  a booking confirmation email containing your e-ticket and
                  itinerary.
                </li>
                <li>
                  <b>Ticket Usage Order:</b> Tickets must be used in the order
                  issued. Failure to use one flight segment may result in the
                  cancellation of subsequent segments by the airline.
                </li>
                <li>
                  <b>Fare Conditions:</b> Cheaper fares, promotional fares, or
                  discounted tickets often carry stricter conditions. Such tickets
                  may be <b>non-refundable and/or non-changeable</b>.
                </li>
                <li>
                  <b>Customer Responsibility:</b> Teleport Now is not responsible for
                  losses or denied boarding caused by incorrect personal or travel
                  details provided by customers.
                </li>
                <li>
                  <b>Airline Responsibility:</b> If airlines cancel or change
                  flights, the airline’s terms and conditions apply. <b>
                    Refunds, rebookings, or compensation in the event of
                    airline-initiated cancellations, delays, or schedule changes
                    are strictly the responsibility of the airline.
                  </b>{" "}
                  Teleport Now only facilitates the booking and is not responsible for
                  disruptions.
                </li>
                <li>
                  <b>Refund Timelines:</b> Where refunds are approved by airlines,
                  the processing timeline is subject to airline and payment
                  processor schedules and may take up to <b>90 days</b>.
                </li>
                <li>
                  <b>Fraud Prevention:</b> Teleport Now may cancel pending or confirmed
                  bookings, or withhold refunds, if chargebacks, suspected fraud,
                  or abuse are involved.
                </li>
                <li>
                  <b>Duplicate/Mistaken Bookings:</b> Teleport Now is not liable for
                  duplicate or mistaken bookings.
                </li>
                <li>
                  <b>Immigration:</b> Teleport Now is not liable if you are denied entry
                  or delayed by immigration authorities.
                </li>
                <li>
                  <b>Repricing & Errors:</b> Teleport Now reserves the right to cancel
                  or correct bookings in the event of pricing errors, technical
                  glitches, or system faults, whether or not the error was apparent
                  to the customer.
                </li>
              </ul>

              <hr />

              {/* ELIGIBILITY */}
              <h2>Eligibility and User Representation</h2>
              <p>By using Teleport Now’s Services, you represent, warrant, and covenant that:</p>
              <ul>
                <li>
                  You are at least 18 years of age (or the age of legal majority in
                  your jurisdiction), and have full legal capacity and authority to
                  enter into and perform your obligations under this Agreement.
                </li>
                <li>
                  You are not prohibited from accessing or using the Services under
                  the laws of the Republic of Singapore or any applicable foreign
                  jurisdiction.
                </li>
                <li>
                  You will comply with all applicable laws and regulations in
                  connection with your use of the Services.
                </li>
                <li>
                  If you are using the Services on behalf of a company or other
                  legal entity, you represent and warrant that you have full
                  authority to bind such entity to these Terms, and in such case,
                  all references to “you” and “your” shall include that entity.
                </li>
              </ul>

              <hr />

              {/* ACCOUNT REGISTRATION */}
              <h2>Account Registration, Responsibilities, and KYC Requirements</h2>
              <p>
                To use Teleport Now, you must create an account. You represent and
                warrant that all information provided during account registration
                and use of the Services is true, accurate, current, and complete.
                You further agree to:
              </p>
              <ul>
                <li>Maintain the confidentiality and security of your account credentials.</li>
                <li>Accept full responsibility for all activities conducted under your account, whether authorized or unauthorized.</li>
                <li>Notify Teleport Now immediately of any suspected or actual unauthorized use of your account.</li>
              </ul>
              <p>
                As part of our compliance obligations, Teleport Now users may be
                required to undergo <b>Know Your Customer (KYC)</b> or <b>eKYC</b>{" "}
                verification. Teleport Now reserves the right to reject, suspend, or
                terminate accounts that fail to pass identity verification or
                submit fraudulent information.
              </p>
              <p>
                Teleport Now may, at its sole discretion, suspend or terminate your
                account for:
              </p>
              <ul>
                <li>Suspected fraudulent or illegal activity.</li>
                <li>Abusive conduct on the platform.</li>
                <li>False or misleading information provided during registration or verification.</li>
                <li>Breach of these Terms.</li>
              </ul>
              <p>
                Teleport Now also reserves the right to report any suspicious activity
                to the relevant authorities where required by law.
              </p>

              <hr />

              {/* PRICING AND PAYMENT */}
              <h2>Pricing and Payment</h2>
              <h3>Pricing</h3>
              <p>
                All prices displayed are in <b>United States Dollars (USD)</b>{" "}
                unless otherwise stated. Currency conversions may use real-time
                exchange rates, and final charges may vary due to bank fees,
                payment processor charges, or dynamic pricing from third-party
                providers. Teleport Now reserves the right to modify pricing at any
                time without prior notice.
              </p>

              <h3>Payment Methods</h3>
              <p>
                Teleport Now accepts secure payment methods processed through trusted{" "}
                <b>third-party payment processors</b>.
              </p>
              <p>
                By submitting payment information, you authorize Teleport Now to process
                and store payment details in accordance with our Privacy Policy.
              </p>

              <h3>Payment Liability Disclaimer</h3>
              <p>
                While Teleport Now takes reasonable steps to ensure secure and accurate
                payment processing, our liability for payment-related issues
                (including delays, errors, or disputes) is limited to the amount of
                the transaction in question. Teleport Now is not liable for indirect or
                consequential losses resulting from third-party payment processor
                errors or failures.
              </p>

              <hr />

               {/* CHARGEBACKS */}
              <h2>Chargebacks</h2>
              <p>
                If you initiate a chargeback with your card issuer or payment
                provider, you remain liable to Teleport Now for the full amount of the
                transaction and any associated fees or penalties imposed on
                Teleport Now by the payment processor.
              </p>
              <p>
                If the chargeback is resolved in Teleport Now’s favor, you agree to
                reimburse Teleport Now for the disputed amount and all related costs
                within <b>10 business days</b> of notification.
              </p>
              <p>
                Teleport Now reserves the right to suspend or terminate your access to
                the Services if repeated or fraudulent chargebacks are made. In
                cases of suspected fraud or abuse, Teleport Now may pursue collection
                efforts and/or legal action to recover amounts owed,{" "}
                <b>in accordance with applicable laws</b>, including reasonable
                legal fees and costs incurred.
              </p>

              <hr />

              {/* REFUNDS */}
              <h2>Refunds and Cancellations</h2>
              <h3>eSIM Refunds</h3>
              <p>Refunds are strictly provided only under the following conditions:</p>
              <ol>
                <li>You have submitted a refund request within <b>30 days</b> of the issue giving rise to your claim.</li>
                <li>You have cooperated with our support team’s efforts to resolve the issue.</li>
                <li>
                  If you have activated the Services but are unable to use them due
                  to an error or omission on the part of Teleport Now, or because
                  Teleport Now has retired the Service, we will either:
                  <ul>
                    <li>Provide a replacement Service, or</li>
                    <li>Refund the full amount of your purchase.</li>
                  </ul>
                </li>
              </ol>
              <p>
                Additional requirements:
                <ul>
                  <li>Teleport Now was unable to restore your access to the purchased Service within <b>10 days</b> of receipt of your refund request.</li>
                  <li>Refund requests can be submitted via <b>support@teleportapp.sg</b>, in-app chat, or web-based chat.</li>
                  <li>Support may request screenshots or device settings to verify the issue.</li>
                  <li>Where eligible, you may choose to receive refunds as <b>Telly Credits</b> or via your original payment method. Credit back may take up to <b>30 business days</b>.</li>
                </ul>
              </p>

              <h3>Unauthorized Purchases</h3>
              <p>
                If you suspect unauthorized purchases have been made with your
                Account, notify our support team immediately. Teleport Now reserves the
                right to suspend any account associated with fraudulent activity.
                Whether a refund is payable is at Teleport Now’s sole discretion.
              </p>

              <h3>Incorrect Charges</h3>
              <p>
                If you believe you have been incorrectly charged, you must contact
                our support team within <b>30 days</b> of incurring the charge,
                providing details of the discrepancy. We will investigate, and
                whether a refund is payable will be at Teleport Now’s sole discretion.
              </p>

              <h3>Flights</h3>
              <p>
                Refunds for flights are strictly processed in accordance with the
                respective airline’s terms and conditions. Certain fares may be
                non-refundable, partially refundable, or subject to special refund
                rules. Teleport Now cannot override these policies.
              </p>
              <p>
                Refund timelines are subject to airline and payment processor
                schedules and may take up to <b>90 days</b>.
              </p>
              <p>
                Teleport Now may cancel or withhold refunds where chargebacks,
                suspected fraud, or abuse are involved.
              </p>

              <hr />

              {/* DIGITAL WALLET */}
              <h2>Digital Wallet and Promotional Credits</h2>
              <ul>
                <li>Non-transferable and non-cashable.</li>
                <li><b>Expire 12 months</b> from the date of issuance.</li>
                <li>May be withdrawn or revoked without notice in cases of abuse or fraudulent behavior.</li>
                <li>Strictly prohibited from resale.</li>
              </ul>
              <p>
                Teleport Now reserves all rights related to the issuance, revocation,
                or modification of digital credits and promotional incentives at
                its sole discretion.
              </p>

              <hr />

              {/* USER CONDUCT */}
              <h2>User Conduct and Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul>
                <li>Use the Services for illegal, obscene, or exploitative purposes.</li>
                <li>Introduce viruses or disrupt service functionality.</li>
                <li>Harvest or collect personal information without consent.</li>
                <li>Attempt to bypass security measures or impersonate others.</li>
              </ul>
              <p>
                Teleport Now may suspend or terminate access without notice if misuse
                is detected. We also reserve the right to cooperate with law
                enforcement and disclose user information where required by law.
              </p>

              <hr />

              {/* DATA PRIVACY */}
              <h2>Data Privacy and Tracking</h2>
              <p>
                By using our Services, you consent to our collection and processing
                of your personal data in accordance with our Privacy Policy and the{" "}
                <b>Personal Data Protection Act (PDPA) of Singapore</b>. This
                includes name, address, phone number, email, device ID, IP address,
                network details, GPS location, and service usage behavior.
              </p>
              <p>
                Your data may also be used for <b>marketing purposes</b> (emails,
                SMS, push notifications, in-app messages). You may opt out by
                unsubscribing or contacting support@teleportapp.sg.
              </p>

              <hr />

              {/* INTELLECTUAL PROPERTY */}
              <h2>Intellectual Property</h2>
              <p>
                All content on the Teleport Now platform, including logos, interfaces,
                software, and trademarks, is the intellectual property of Teleport Now
                or its licensors. You may not copy, reuse, distribute, or reproduce
                such content without explicit written permission.
              </p>

              <hr />

              {/* DISCLAIMERS */}
              <h2>Disclaimers and Limitation of Liability</h2>
              <p>
                The Services are provided on an "as-is" and "as-available" basis. To
                the maximum extent permitted by law:
              </p>
              <ul>
                <li>Teleport Now disclaims implied warranties but provides services with reasonable skill and care.</li>
                <li>Teleport Now makes no guarantees regarding uninterrupted service or absolute accuracy of data.</li>
                <li>Teleport Now’s liability for any claim is limited to the amount you paid for the specific transaction giving rise to the claim.</li>
                <li>Teleport Now is not liable for indirect, incidental, punitive, special, or consequential damages, unless otherwise required by law.</li>
              </ul>

              <h3>Force Majeure</h3>
              <p>
                Teleport Now is not liable for delays, failures, or disruptions caused
                by circumstances beyond our reasonable control, including but not
                limited to: wars, acts of terrorism, civil unrest, natural
                disasters, epidemics or pandemics, government sanctions, labor
                disputes, power or internet outages, or restrictions imposed by
                law.
              </p>

              <hr />

              {/* TERMINATION */}
              <h2>Termination and Data Retention</h2>
              <p>Teleport Now may suspend or terminate access for:</p>
              <ul>
                <li>Violation of these Terms.</li>
                <li>Legal or regulatory requirement.</li>
                <li>Fraudulent or suspicious behavior.</li>
              </ul>
              <p>
                Upon termination, your data may be deleted except where retention
                is required by law.
              </p>
              <p>
                <b>Data Retention:</b> In cases involving fraud, misuse, or
                violations of Singaporean law, Teleport Now reserves the right to
                retain your personal data, including transactional and
                communication records, for up to <b>five (5) years</b>, strictly
                for the purposes of:
              </p>
              <ul>
                <li>Compliance with legal and regulatory obligations.</li>
                <li>Fraud detection, prevention, and investigation.</li>
                <li>Cooperation with law enforcement authorities and government agencies.</li>
              </ul>

              <hr />

              {/* GOVERNING LAW */}
              <h2>Governing Law and Dispute Resolution</h2>
              <p>
                These Terms are governed by the laws of the Republic of Singapore.
              </p>
              <p>Any disputes shall be resolved either by:</p>
              <ul>
                <li>Arbitration administered by the <b>Singapore International Arbitration Centre (SIAC)</b>, or</li>
                <li>The competent courts of Singapore.</li>
              </ul>

              <hr />

              {/* AMENDMENTS */}
              <h2>Amendments</h2>
              <p>
                We may modify these Terms at any time without prior notice. Changes
                become effective immediately upon publication on our platform or
                website. Continued use of the Services after modifications
                constitutes acceptance.
              </p>

              <hr />

              {/* CONSUMER PROTECTION */}
              <h2>Consumer Protection</h2>
              <p>
                Nothing in these Terms affects your statutory rights under
                applicable consumer protection laws.
              </p>

              <hr />

              {/* CONTACT */}
              <h2>Contact Information</h2>
              <p>
                <b>TELEPORT NOW (SG) PTE. LTD.</b> <br />
                UEN: 202519519R <br />
                Address: 60 Paya Lebar Road, #06-28 Paya Lebar Square, Singapore
                409051 <br />
                Email: legal@teleportapp.sg
              </p>
            </div>
          </div>
        </div>
      </div>
    <Footer />
    </>
  );
};

export default TermsCondition;
